import { Link } from 'react-router-dom'
import { Shield, Globe, Database, Zap, ArrowRight, Terminal, Code2, Network, CheckCircle, AlertTriangle, Linkedin, Twitter, Search, Lock, FileText, Target, Fingerprint } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import SubscriptionTiers from '@/components/SubscriptionTiers'
import { SEOHead } from '@/components/SEOHead'

const features = [
  {
    icon: Shield,
    title: 'BreachINT',
    description: 'Advanced breach intelligence with graph visualization and correlation analysis across multiple databases',
    color: 'text-red-400'
  },
  {
    icon: Globe,
    title: 'GEOINT Wayback',
    description: 'Historical geospatial intelligence with satellite imagery analysis and location-based investigations',
    color: 'text-green-400'
  },
  {
    icon: Database,
    title: 'Social Intelligence',
    description: 'Comprehensive social media monitoring with sentiment analysis and entity extraction',
    color: 'text-blue-400'
  },
  {
    icon: Network,
    title: 'Advanced Analytics',
    description: 'AI-powered threat detection with automated pattern recognition and correlation',
    color: 'text-purple-400'
  }
]

const stats = [
  { label: 'Data Sources', value: '50+', color: 'text-cyan-400' },
  { label: 'Countries', value: '190+', color: 'text-green-400' },
  { label: 'Investigations', value: '10K+', color: 'text-yellow-400' },
  { label: 'Threats Detected', value: '1M+', color: 'text-red-400' }
]

export default function Landing() {
  return (
    <>
      <SEOHead
        title="Sycek OSINT - Advanced Cyber Intelligence & OSINT Platform | Threat Detection"
        description="Unlock the future of cyber intelligence & OSINT. Master advanced OSINT and cyber intelligence with our immersive, AI-powered platform designed for security professionals, analysts, and investigators. BreachINT, GEOINT, SOCINT, and more."
        keywords="OSINT, open source intelligence, cyber intelligence, threat intelligence, breach detection, data breach, leak intelligence, geospatial intelligence, GEOINT, social media intelligence, SOCINT, digital forensics, cybersecurity, threat monitoring, security investigation, network analysis, intelligence platform, cyber threat detection, security analytics, vulnerability assessment, penetration testing tools, security research, digital investigation, cyber forensics, threat hunting, security operations, incident response, security intelligence, cyber defense, information security, security automation, threat analysis, security analytics platform, breach intelligence, data leak detection, identity correlation, maritime intelligence, air traffic intelligence, WiFi intelligence, satellite imagery analysis, historical geospatial data, social media monitoring, real-time threat detection, AI-powered security, machine learning security, security professionals, cybersecurity analysts, investigators, law enforcement, private investigators, security consultants, Bangalore, India"
        canonical="/"
      />
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      
      {/* Floating Code Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-cyan-500/10 font-mono text-xs animate-pulse">{'<OSINT>'}</div>
        <div className="absolute top-40 right-20 text-green-500/10 font-mono text-xs animate-pulse delay-1000">0xDEADBEEF</div>
        <div className="absolute bottom-20 left-20 text-purple-500/10 font-mono text-xs animate-pulse delay-2000">[INTEL]</div>
        <div className="absolute bottom-40 right-10 text-red-500/10 font-mono text-xs animate-pulse delay-3000">./hack</div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                // Fallback if logo not found
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50 hover:border-cyan-400 hover:shadow-[0_0_10px_rgba(34,211,238,0.3)] transition-all">
                <Code2 className="w-4 h-4 mr-2" />
                Access Portal
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white border-0 shadow-lg shadow-cyan-500/60 hover:shadow-cyan-500/80 transition-all">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section with OSINT Banner */}
      <section className="relative z-10 overflow-hidden">
        {/* OSINT Themed Banner Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/30 via-purple-900/20 to-black">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#06b6d440_1px,transparent_1px),linear-gradient(to_bottom,#06b6d440_1px,transparent_1px)] bg-[size:3rem_3rem]"></div>
          
          {/* Floating OSINT Icons */}
          <div className="absolute top-20 left-10 text-cyan-400/20 animate-pulse">
            <Network className="w-16 h-16" />
          </div>
          <div className="absolute top-32 right-20 text-green-400/20 animate-pulse delay-1000">
            <Search className="w-12 h-12" />
          </div>
          <div className="absolute bottom-32 left-20 text-purple-400/20 animate-pulse delay-2000">
            <Shield className="w-14 h-14" />
          </div>
          <div className="absolute bottom-20 right-10 text-red-400/20 animate-pulse delay-3000">
            <Target className="w-12 h-12" />
          </div>
        </div>

        <div className="relative container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-6xl mx-auto">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.jpeg" 
                alt="Sycek Logo" 
                className="h-24 w-24 md:h-32 md:w-32 object-contain drop-shadow-[0_0_25px_rgba(34,197,94,0.8)] animate-pulse"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
            
            {/* Status Badge */}
            <div className="flex justify-center mb-6">
              <Badge className="bg-green-500/20 text-green-400 border border-green-500/50 font-mono shadow-lg shadow-green-500/30 px-4 py-2">
                <Zap className="w-3 h-3 mr-2 drop-shadow-[0_0_5px_rgba(34,197,94,0.8)]" />
                [SYSTEM READY] AI-Powered Intelligence Platform
              </Badge>
            </div>
            
            {/* Main Heading */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-green-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(34,197,94,0.6)]">
                  Unlock the Future of
                </span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 via-green-400 to-emerald-400 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(34,211,238,0.6)]">
                  Cyber Intel & OSINT
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto font-light leading-relaxed">
                <span className="text-green-400 font-mono drop-shadow-[0_0_10px_rgba(34,197,94,0.6)]">[SYCEK@ROOT]#</span> Master advanced OSINT and cyber intelligence with our immersive, AI-powered platform designed for security professionals, analysts, and investigators.
              </p>
            </div>

            {/* OSINT Feature Icons Grid */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 mb-12 max-w-4xl mx-auto">
              {[
                { icon: Search, label: 'Search', color: 'text-cyan-400' },
                { icon: Shield, label: 'Protect', color: 'text-green-400' },
                { icon: Network, label: 'Network', color: 'text-purple-400' },
                { icon: Lock, label: 'Security', color: 'text-blue-400' },
                { icon: Target, label: 'Target', color: 'text-red-400' },
                { icon: Fingerprint, label: 'Identity', color: 'text-yellow-400' }
              ].map((item, index) => (
                <div key={index} className="flex flex-col items-center p-4 rounded-lg bg-gray-900/50 border border-cyan-500/20 hover:border-cyan-500/60 hover:bg-gray-900/80 transition-all duration-300 group">
                  <item.icon className={`w-8 h-8 md:w-10 md:h-10 mb-2 ${item.color} group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_currentColor]`} />
                  <span className="text-xs text-gray-300 font-mono group-hover:text-cyan-400 transition-colors">{item.label}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white text-lg px-8 py-6 shadow-lg shadow-cyan-500/60 hover:shadow-cyan-500/80 transition-all border border-cyan-400/30">
                  <Terminal className="w-5 h-5 mr-2 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                  Initialize System
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-cyan-500/60 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-400 hover:shadow-[0_0_15px_rgba(34,211,238,0.4)] text-lg px-8 py-6 transition-all">
                  <FileText className="w-5 h-5 mr-2" />
                  Explore Features
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {stats.map((stat, index) => (
                <Card key={index} className="bg-gray-900 border border-cyan-500/30 backdrop-blur-sm hover:border-cyan-500/60 hover:shadow-[0_0_15px_rgba(34,211,238,0.3)] transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className={`text-4xl md:text-5xl font-bold mb-2 ${stat.color} font-mono drop-shadow-[0_0_10px_currentColor]`}>
                      {stat.value}
                    </div>
                    <div className="text-gray-300 text-sm font-mono">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-500/20 text-purple-400 border border-purple-500/50 font-mono shadow-lg shadow-purple-500/20">
              [MODULES LOADED]
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(34,211,238,0.5)]">
                Intelligence Modules
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light">
              Advanced OSINT capabilities for comprehensive threat intelligence and investigation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="bg-gray-900 border border-cyan-500/30 hover:border-cyan-500/70 backdrop-blur-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <CardHeader className="relative z-10">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg ${feature.color.includes('red') ? 'shadow-red-500/30' : feature.color.includes('green') ? 'shadow-green-500/30' : feature.color.includes('blue') ? 'shadow-blue-500/30' : 'shadow-purple-500/30'}`}>
                    <feature.icon className={`w-6 h-6 ${feature.color} drop-shadow-[0_0_8px_currentColor]`} />
                  </div>
                  <CardTitle className="text-white text-lg font-mono group-hover:text-cyan-400 transition-colors">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <CardDescription className="text-gray-300 text-sm leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
                Execution Protocol
              </span>
            </h2>
            <p className="text-xl text-gray-400 font-light">
              Three-step process for advanced intelligence gathering
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                title: 'Input Query',
                description: 'Enter emails, domains, usernames, or identifiers across multiple data sources with advanced filtering',
                color: 'from-cyan-500 to-blue-600',
                glow: 'shadow-cyan-500/30'
              },
              {
                step: '02',
                title: 'AI Analysis',
                description: 'Our advanced AI engine processes data, identifies connections, patterns, and potential threat indicators',
                color: 'from-purple-500 to-pink-600',
                glow: 'shadow-purple-500/30'
              },
              {
                step: '03',
                title: 'Visualize Results',
                description: 'View interactive graph networks, generate comprehensive reports, and export data for further analysis',
                color: 'from-green-500 to-cyan-600',
                glow: 'shadow-green-500/30'
              }
            ].map((item, index) => (
              <Card key={index} className="bg-gray-900 border border-cyan-500/30 backdrop-blur-sm relative overflow-hidden hover:border-cyan-500/70 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)] transition-all duration-300 group">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${item.color} shadow-lg ${item.glow}`}></div>
                <CardContent className="p-6 text-center">
                  <div className={`text-6xl font-bold mb-4 font-mono bg-gradient-to-r ${item.color} bg-clip-text text-transparent drop-shadow-[0_0_15px_currentColor]`}>{item.step}</div>
                  <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-cyan-400 transition-colors font-mono">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <SubscriptionTiers />

      {/* CTA Section */}
      <section className="relative z-10 py-20 bg-gradient-to-br from-cyan-900/30 via-purple-900/30 to-black border-t border-cyan-500/20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <Badge className="mb-6 bg-green-500/20 text-green-400 border border-green-500/50 font-mono shadow-lg shadow-green-500/30">
              <CheckCircle className="w-3 h-3 mr-2 drop-shadow-[0_0_5px_rgba(74,222,128,0.8)]" />
              [SYSTEM READY]
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(34,211,238,0.5)]">
                Ready to Initialize?
              </span>
            </h2>
            <p className="text-xl text-gray-200 mb-8 font-light">
              Join security professionals and investigators who trust Sycek OSINT for advanced threat intelligence
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white text-lg px-8 py-6 shadow-lg shadow-cyan-500/60 hover:shadow-cyan-500/80 transition-all border border-cyan-400/30">
                  <Terminal className="w-5 h-5 mr-2 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-cyan-500/60 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-400 hover:shadow-[0_0_15px_rgba(34,211,238,0.4)] text-lg px-8 py-6 transition-all">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Contact Intelligence Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-black border-t border-cyan-500/30 py-12 shadow-[0_-4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/logo.jpeg" 
                  alt="Sycek Logo" 
                  className="h-8 w-8 object-contain drop-shadow-[0_0_12px_rgba(34,197,94,0.6)]"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <h3 className="text-xl font-bold bg-gradient-to-r from-green-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,197,94,0.5)] font-mono">
                  SYCEK OSINT
                </h3>
              </div>
              <p className="text-gray-300 text-sm font-mono mb-4">
                Advanced cyber intelligence platform for security professionals.
              </p>
              <div className="flex items-center space-x-4 mt-4">
                <a 
                  href="https://www.linkedin.com/company/sycek-cyber-intelligence/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-green-400 transition-colors drop-shadow-[0_0_8px_rgba(34,211,238,0.5)] hover:drop-shadow-[0_0_12px_rgba(34,197,94,0.8)]"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href="https://x.com/Sycek_osint" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-green-400 transition-colors drop-shadow-[0_0_8px_rgba(34,211,238,0.5)] hover:drop-shadow-[0_0_12px_rgba(34,197,94,0.8)]"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)] font-mono">Modules</h4>
              <ul className="space-y-2 text-gray-300 text-sm font-mono">
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">BreachINT</li>
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">GEOINT Wayback</li>
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Social Intelligence</li>
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Threat Detection</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)] font-mono">Company</h4>
              <ul className="space-y-2 text-gray-300 text-sm font-mono">
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">About</li>
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Contact</li>
                <li className="hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Support</li>
                <Link to="/api-docs" className="block hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">API Documentation</Link>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)] font-mono">Legal</h4>
              <ul className="space-y-2 text-gray-300 text-sm font-mono">
                <Link to="/privacy-policy" className="block hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Privacy Policy</Link>
                <Link to="/terms-of-service" className="block hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Terms of Service</Link>
                <Link to="/security" className="block hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Security</Link>
                <Link to="/compliance" className="block hover:text-cyan-400 hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all cursor-pointer">Compliance</Link>
              </ul>
            </div>
          </div>
          <div className="border-t border-cyan-500/30 pt-8 text-center space-y-2">
            <p className="text-gray-400 text-sm font-mono">
              &copy; 2026 SYCEK OSINT. All rights reserved. <span className="text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">[SYSTEM: ACTIVE]</span>
            </p>
            <p className="text-gray-400 text-sm font-mono">
              Made with <span className="text-red-400">❤️</span> from <span className="text-green-400">India</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}
